import { z } from 'zod';
import { ApiErrorCodeSchema, DateOnlySchema } from './common.js';

/** Inventory/registry records (org_records) — see collections.ts for the field catalogue. */

/** 'draft' = created by AI inventory import, awaiting human review/approval. */
export const RecordStatusSchema = z.enum(['active', 'archived', 'draft']);
export type RecordStatus = z.infer<typeof RecordStatusSchema>;

/** Wizard answers keyed by field key; validated against the collection definition server-side. */
export const RecordDataSchema = z.record(z.string(), z.unknown());
export type RecordData = z.infer<typeof RecordDataSchema>;

export const OrgRecordSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  collection: z.string(),
  name: z.string(),
  data: RecordDataSchema,
  status: RecordStatusSchema,
  createdBy: z.string().nullable(),
  createdByName: z.string().nullable(),
  updatedBy: z.string().nullable(),
  updatedByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OrgRecord = z.infer<typeof OrgRecordSchema>;

export const CreateRecordInputSchema = z.object({
  collection: z.string().min(1),
  data: RecordDataSchema,
});
export type CreateRecordInput = z.infer<typeof CreateRecordInputSchema>;

export const UpdateRecordInputSchema = z.object({
  data: RecordDataSchema.optional(),
  status: RecordStatusSchema.optional(),
  /**
   * Move the record to another collection (e.g. an imported item filed as
   * hardware that is really software). Data is re-fitted to the target's
   * fields; values that do not fit are dropped.
   */
  collection: z.string().min(1).optional(),
  /**
   * Merge-patch: shallow-merge `data` over the stored data before validation —
   * keys present overwrite, a `null` value removes the key, everything else is
   * kept. The merged object is then strict-validated like a full PATCH.
   */
  merge: z.boolean().default(false),
});
export type UpdateRecordInput = z.infer<typeof UpdateRecordInputSchema>;

/** One operation of a batch — no collection moves; those stay on the single PATCH. */
export const RecordBatchItemSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('create'),
    collection: z.string().min(1),
    data: RecordDataSchema,
  }),
  z.object({
    op: z.literal('update'),
    id: z.string().min(1),
    data: RecordDataSchema.optional(),
    status: RecordStatusSchema.optional(),
    merge: z.boolean().default(false),
  }),
]);
export type RecordBatchItem = z.infer<typeof RecordBatchItemSchema>;

export const RecordBatchInputSchema = z.object({
  items: z.array(RecordBatchItemSchema).min(1).max(100),
});
export type RecordBatchInput = z.infer<typeof RecordBatchInputSchema>;

export const RecordBatchResultItemSchema = z.object({
  index: z.number().int(),
  op: z.enum(['create', 'update']),
  status: z.enum(['created', 'updated', 'failed']),
  record: OrgRecordSchema.nullable(),
  error: z
    .object({
      code: ApiErrorCodeSchema,
      message: z.string(),
      details: z.record(z.string(), z.unknown()).optional(),
    })
    .nullable(),
});
export type RecordBatchResultItem = z.infer<typeof RecordBatchResultItemSchema>;

/** Partial success by design: failed items are reported per index, the rest are applied. */
export const RecordBatchResultSchema = z.object({
  results: z.array(RecordBatchResultItemSchema),
  createdCount: z.number().int(),
  updatedCount: z.number().int(),
  failedCount: z.number().int(),
});
export type RecordBatchResult = z.infer<typeof RecordBatchResultSchema>;

/** Level of access a grant confers (access reviews, migration 0017). */
export const ACCESS_LEVELS = ['admin', 'privileged', 'standard', 'read_only'] as const;
export const AccessLevelSchema = z.enum(ACCESS_LEVELS);
export type AccessLevel = z.infer<typeof AccessLevelSchema>;
export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  admin: 'Admin',
  privileged: 'Privileged',
  standard: 'Standard user',
  read_only: 'Read-only',
};

/** A grant's access level, or '' when it has not been recorded. */
export const AssignmentAccessLevelSchema = z.union([z.literal(''), AccessLevelSchema]);
export type AssignmentAccessLevel = z.infer<typeof AssignmentAccessLevelSchema>;

/** How often a grant must be re-certified. */
export const REVIEW_FREQUENCIES = ['quarterly', 'six_monthly', 'annual', 'none'] as const;
export const ReviewFrequencySchema = z.enum(REVIEW_FREQUENCIES);
export type ReviewFrequency = z.infer<typeof ReviewFrequencySchema>;
export const REVIEW_FREQUENCY_LABELS: Record<ReviewFrequency, string> = {
  quarterly: 'Quarterly',
  six_monthly: 'Six-monthly',
  annual: 'Annual',
  none: 'No review',
};

export const RecordAssignmentSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  recordId: z.string(),
  recordName: z.string(),
  collection: z.string(),
  recordData: RecordDataSchema,
  userId: z.string(),
  userName: z.string(),
  userEmail: z.string(),
  notes: z.string(),
  assignedBy: z.string().nullable(),
  assignedByName: z.string().nullable(),
  assignedAt: z.string(),
  revokedAt: z.string().nullable(),
  /** '' when the level was never recorded (grants that pre-date access reviews). */
  accessLevel: AssignmentAccessLevelSchema,
  /** Free-text role name, e.g. 'Global admin', 'Maintainer', 'Vault admin'. */
  accessRole: z.string(),
  /** Who allowed the access (may differ from assignedBy). */
  approvedBy: z.string().nullable(),
  approvedByName: z.string().nullable(),
  grantedOn: z.string().nullable(),
  reviewFrequency: ReviewFrequencySchema,
  /** Always stored, never derived at read time; null only when frequency is 'none'. */
  nextReviewOn: z.string().nullable(),
  lastReviewedAt: z.string().nullable(),
  lastReviewedByName: z.string().nullable(),
  /** nextReviewOn is in the past (computed server-side against today). */
  overdue: z.boolean(),
});
export type RecordAssignment = z.infer<typeof RecordAssignmentSchema>;

export const AssignRecordInputSchema = z.object({
  userId: z.string().min(1),
  notes: z.string().max(500).default(''),
  accessLevel: AssignmentAccessLevelSchema.default(''),
  accessRole: z.string().max(100).default(''),
  /** Must be an org member when set. */
  approvedBy: z.string().nullable().optional(),
  /** Defaults to today. */
  grantedOn: DateOnlySchema.optional(),
  reviewFrequency: ReviewFrequencySchema.default('annual'),
});
export type AssignRecordInput = z.infer<typeof AssignRecordInputSchema>;

/** Edit a live grant's access details (PATCH …/assignments/:assignmentId). */
export const UpdateAssignmentInputSchema = z.object({
  accessLevel: AssignmentAccessLevelSchema.optional(),
  accessRole: z.string().max(100).optional(),
  approvedBy: z.string().nullable().optional(),
  grantedOn: DateOnlySchema.optional(),
  reviewFrequency: ReviewFrequencySchema.optional(),
  notes: z.string().max(500).optional(),
});
export type UpdateAssignmentInput = z.infer<typeof UpdateAssignmentInputSchema>;

/** One draft record created by AI inventory import. */
export const InventoryImportCreatedSchema = z.object({
  recordId: z.string(),
  collection: z.string(),
  name: z.string(),
  confidence: z.enum(['high', 'medium', 'low']),
  /** Field keys the AI filled by inference rather than from the document. */
  assumptions: z.array(z.string()),
});
export type InventoryImportCreated = z.infer<typeof InventoryImportCreatedSchema>;

export const InventoryImportFileSchema = z.object({
  fileName: z.string(),
  status: z.enum(['succeeded', 'failed', 'skipped']),
  /** Failure/skip reason; null when succeeded. */
  error: z.string().nullable(),
  created: z.array(InventoryImportCreatedSchema),
  /** Items the AI proposed but that were not created, with the reason. */
  skipped: z.array(z.object({ name: z.string(), reason: z.string() })),
});
export type InventoryImportFile = z.infer<typeof InventoryImportFileSchema>;

export const InventoryImportResultSchema = z.object({
  files: z.array(InventoryImportFileSchema),
  createdTotal: z.number().int(),
});
export type InventoryImportResult = z.infer<typeof InventoryImportResultSchema>;

/**
 * Import a point-in-time snapshot of a collection into a response as a
 * markdown table ("dynamic import"; audited as response.snapshot_imported).
 */
export const RecordSnapshotInputSchema = z.object({
  collection: z.string().min(1),
  mode: z.enum(['append', 'replace']).default('append'),
});
export type RecordSnapshotInput = z.infer<typeof RecordSnapshotInputSchema>;
